<template>
  <div class="video-task-player">
    <vue-vimeo-player
      class="video-frame"
      ref="player"
      :video-id="videoId"
      @ready="onReady"
      @play="onPlay"
      @pause="onPause"
      @ended="onEnded"
      @seeked="onSeeked"
      @timeupdate="onTimeUpdate"
    />
    <div v-if="hasCover && isCoverVisible" class="iframe-cover" @click="play" />
  </div>
</template>

<script>
import { vueVimeoPlayer } from 'vue-vimeo-player';
import Content from '@/services/Api/content';
import gtmUtils from '@/mixins/gtmUtils.js';

export default {
  name: 'VideoTaskPlayer',
  mixins: [gtmUtils],
  components: {
    vueVimeoPlayer,
  },

  props: {
    data: {
      type: Object,
      required: true,
    },
    hasCover: {
      type: Boolean,
      default: false,
    },
    id: {
      type: [String, Number],
      default: 'vimeo_id',
    },
  },

  data() {
    return {
      duration: 0,
      currentSecond: 0,
      isPlaying: false,
      isTaskCompleted: false,
      isCoverVisible: this.hasCover,
      frameCss: {
        height: '100%',
        width: '100%',
        top: '0',
        left: '0',
        position: 'absolute',
      },
      watchedSeconds: new Set(),
      postedIntervals: new Set(),
      previousSecond: null,
      logFlag: [],
      isSeeked: false,
      isSkipLogged: false,
      shouldResetWatchProgress: true,
    };
  },

  computed: {
    videoId() {
      if (!this.data.embedVideo) return '';
      return this.data.embedVideo.substring(this.data.embedVideo.lastIndexOf('/') + 1);
    },

    contentId() {
      return this.data.contentId ?? this.data.videoId;
    },

    taskId() {
      return this.data.taskId;
    },
  },

  methods: {
    async onReady(e) {
      Object.keys(this.frameCss).every(item => (e.element.style[item] = this.frameCss[item]));

      this.duration = await this.$refs.player.player.getDuration();
      this.$emit('ready');
    },

    play() {
      this.$refs.player.play();
    },

    pause() {
      this.$refs.player.pause();
    },

    stop() {
      this.$refs.player.pause();
      this.$refs.player.player.setCurrentTime(0);

      this.currentSecond = 0;
      this.previousSecond = null;
      this.isPlaying = false;
      this.isCoverVisible = this.hasCover;
      this.watchedSeconds.clear();
      this.postedIntervals.clear();

      this.$emit('stop');
    },

    jumpTo(second) {
      const safeSecond = Math.max(0, Math.min(Math.floor(second), Math.floor(this.duration || 0)));

      this.$refs.player.player.setCurrentTime(safeSecond);
      this.currentSecond = safeSecond;
      this.previousSecond = null;
      this.watchedSeconds.clear();
      this.postedIntervals.clear();

      this.$emit('jump', safeSecond);
    },

    onPlay() {
      this.isPlaying = true;
      this.isCoverVisible = false;

      setTimeout(() => {
        if (!this.isSeeked) {
          this.logDataLayer('Play');
        }

        this.isSeeked = false;
        this.isSkipLogged = false;
      }, 200);

      this.$emit('play');
    },

    onPause() {
      this.isPlaying = false;
      this.isCoverVisible = this.hasCover;
      setTimeout(() => {
        this.logDataLayer('Pause');
      }, 300);

      this.$emit('pause');
    },

    onEnded() {
      this.isPlaying = false;
      this.isCoverVisible = this.hasCover;

      this.markWatchedSeconds(this.duration || this.currentSecond);
      this.checkCompletedIntervals();

      this.logDataLayer('end');
      this.$emit('ended');
    },

    onSeeked(payload) {
      const seconds = this.getSecondsFromPayload(payload);

      this.isSeeked = true;
      this.currentSecond = seconds;
      this.previousSecond = null;
      this.watchedSeconds.clear();
      this.postedIntervals.clear();

      if (!this.isSkipLogged) {
        this.logDataLayer('Skip');
        this.isSkipLogged = true;
      }

      this.$emit('seeked', seconds);
    },

    onTimeUpdate(payload) {
      const seconds = this.getSecondsFromPayload(payload);
      this.currentSecond = seconds;

      if (this.duration) {
        this.logDataLayer('log', seconds / this.duration);
      }

      if (!this.isPlaying) return;

      this.markWatchedSeconds(seconds);
      this.checkCompletedIntervals();
      this.previousSecond = seconds;

      this.$emit('timeupdate', {
        seconds,
        isTaskCompleted: this.isTaskCompleted,
      });
    },

    getSecondsFromPayload(payload) {
      return Math.floor(payload && payload.seconds ? payload.seconds : 0);
    },

    markWatchedSeconds(currentSecond) {
      if (this.previousSecond === null) {
        this.previousSecond = currentSecond;
        return;
      }

      const diff = currentSecond - this.previousSecond;

      if (diff <= 0 || diff > 2) {
        this.previousSecond = currentSecond;
        return;
      }

      for (let second = this.previousSecond; second < currentSecond; second += 1) {
        this.watchedSeconds.add(second);
      }
    },

    checkCompletedIntervals() {
      const maxSecond = Math.floor(this.duration || this.currentSecond);

      for (let start = 0; start < maxSecond; start += 5) {
        const end = Math.min(start + 5, maxSecond);
        const key = `${start}-${end}`;
        const intervalSeconds = [];

        if (this.postedIntervals.has(key)) continue;

        for (let second = start; second < end; second += 1) {
          intervalSeconds.push(second);
        }

        const isIntervalFullyWatched = intervalSeconds.every(second =>
          this.watchedSeconds.has(second),
        );

        if (isIntervalFullyWatched) {
          this.postInterval(start, end);
        }
      }
    },

    async postInterval(intervalStart, intervalEnd) {
      const key = `${intervalStart}-${intervalEnd}`;

      this.postedIntervals.add(key);

      const payload = {
        contentId: this.contentId,
        taskId: this.taskId,
        intervalStart,
        intervalEnd,
      };

      if (this.shouldResetWatchProgress) {
        payload.resetWatchProgress = true;
      }

      try {
        const res = await Content.postTaskWatchProgress(payload);

        if (this.shouldResetWatchProgress) {
          this.shouldResetWatchProgress = false;
        }

        const isTaskCompleted = Boolean(res?.data?.Data?.isTaskCompleted);

        if (isTaskCompleted) {
          this.isTaskCompleted = true;
          this.$emit('taskCompleted', true);
        }

        this.$emit('intervalPosted', {
          ...payload,
          isTaskCompleted,
        });
      } catch (error) {
        this.postedIntervals.delete(key);

        this.$emit('intervalPostError', {
          payload,
          error,
        });
      }
    },

    logDataLayer(action, percent) {
      let log = '';

      if (action === 'log') {
        if (percent >= 0.1 && percent < 0.25 && !this.logFlag.includes(10)) {
          this.logFlag.push(10);
          log = 10;
        } else if (percent >= 0.25 && percent < 0.5 && !this.logFlag.includes(25)) {
          this.logFlag.push(25);
          log = 25;
        } else if (percent >= 0.5 && percent < 0.75 && !this.logFlag.includes(50)) {
          this.logFlag.push(50);
          log = 50;
        } else if (percent >= 0.75 && percent < 0.9 && !this.logFlag.includes(75)) {
          this.logFlag.push(75);
          log = 75;
        } else if (percent >= 0.9 && !this.logFlag.includes(90)) {
          this.logFlag.push(90);
          log = 90;
        }

        if (log) {
          this.pushDataLayerEvent('video', {
            name: this.data.title || 'Bilinmiyor',
            action: `%${log}`,
            url: this.data.embedVideo || '',
          });
        }
        return;
      }

      if (action === 'end') {
        this.pushDataLayerEvent('video', {
          name: this.data.title || 'Bilinmiyor',
          action: 'Watch to End',
          url: this.data.embedVideo || '',
        });
        return;
      }

      if (action === 'Pause') {
        if (!this.isTaskCompleted) {
          this.pushDataLayerEvent('video', {
            name: this.data.title || 'Bilinmiyor',
            action: 'Pause',
            url: this.data.embedVideo || '',
          });
        }
        return;
      }

      this.pushDataLayerEvent('video', {
        name: this.data.title || 'Bilinmiyor',
        action,
        url: this.data.embedVideo || '',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.video-task-player {
  .video-frame {
    position: relative;
    display: flex;
    width: 100%;
    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover;
    align-items: center;
    overflow: hidden;

    /deep/ div {
      height: 100%;
      width: 100%;
      inset: 0;
      position: absolute;
    }

    &::before {
      content: '';
      display: block;
      padding-top: 56.25%;
    }
  }

  .iframe-cover {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}
</style>
